import React, { memo, useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import API from "Utils/api";
import {
  otaComponentsApi,
  otaFleetsApi,
  otaVehiclesApi,
  deploymentSessionsApi,
} from "../../api";
import { OTADeploymentSession, OTAFleet, OTAVehicle } from "../../types";

// ── Types ─────────────────────────────────────────────────────────────────────

interface RawVT {
  id: string;
  name: string;
  is_deprecated: boolean;
}

// ── Styled components ─────────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const PageTitle = styled.h2`
  font-size: 20px;
  font-weight: 500;
  color: #172a3f;
  margin: 0;
  margin-right: auto;
`;

const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #172a3f;
  margin: 0 0 14px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #e8f0fe;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 14px;
`;

const HealthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 14px;
`;

const MetricCard = styled.div<{ $accent?: string }>`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 4px solid ${({ $accent }) => $accent ?? "#55b8b3"};
  border-radius: 8px;
  padding: 16px 18px;
`;

const MetricLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 6px;
`;

const MetricValue = styled.div<{ $color?: string }>`
  font-size: 28px;
  font-weight: 700;
  color: ${({ $color }) => $color ?? "#172a3f"};
  font-variant-numeric: tabular-nums;
  line-height: 1;
`;

const MetricSub = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 5px;
`;

const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
`;

const BreakdownCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px 18px;
`;

const BreakdownRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;
  border-bottom: 1px solid #e2e8f0;
  &:last-child {
    border-bottom: none;
  }
`;

const BreakdownLabel = styled.span`
  font-size: 13px;
  color: #172a3f;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Dot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  display: inline-block;
  flex-shrink: 0;
`;

const BreakdownCount = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #172a3f;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px;
  color: #94a3b8;
  font-size: 14px;
`;

const FilterLabel = styled.span`
  font-size: 12px;
  color: #606060;
  white-space: nowrap;
`;

const FilterSelect = styled.select`
  font-size: 12px;
  padding: 5px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  color: #172a3f;
  background: #ffffff;
  cursor: pointer;
`;

const ClearBtn = styled.button`
  font-size: 12px;
  color: #f44336;
  background: transparent;
  border: 1px solid #f44336;
  border-radius: 6px;
  padding: 5px 10px;
  cursor: pointer;
  &:hover {
    background: #fff5f5;
  }
`;

const RefreshBtn = styled.button`
  font-size: 12px;
  color: #55b8b3;
  background: transparent;
  border: 1px solid #55b8b3;
  border-radius: 6px;
  padding: 5px 12px;
  cursor: pointer;
  &:hover {
    background: #f0fafa;
  }
`;

const FilterBadge = styled.span`
  font-size: 11px;
  background: #ebf8ff;
  color: #2c5282;
  border: 1px solid #bee3f8;
  border-radius: 12px;
  padding: 2px 8px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 14px;
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 14px;
  color: #374151;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
  &:last-child {
    border-bottom: none;
  }
`;

const Tr = styled.tr`
  &:last-child td {
    border-bottom: none;
  }
`;

const ErrorText = styled.span`
  color: #94a3b8;
  font-style: italic;
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

const connectionColors: Record<string, string> = {
  online: "#22c55e",
  offline: "#6b7280",
  disconnected: "#f59e0b",
  provisioned: "#3b82f6",
};

const sessionStatusColors: Record<string, string> = {
  COMPLETED: "#4caf50",
  IN_PROGRESS: "#2196f3",
  PENDING: "#ff9800",
  FAILED: "#f44336",
  CANCELLED: "#9e9e9e",
};

function countBy<T>(
  arr: T[],
  key: (item: T) => string
): Record<string, number> {
  return arr.reduce<Record<string, number>>((acc, item) => {
    const k = key(item);
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});
}

function unique(arr: string[]): string[] {
  return Array.from(new Set(arr.filter(Boolean)));
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function truncate(str: string, max: number): string {
  return str.length > max ? str.slice(0, max) + "…" : str;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const StatsView = memo(() => {
  const [loading, setLoading] = useState(true);
  const [fleets, setFleets] = useState<OTAFleet[]>([]);
  const [vehicles, setVehicles] = useState<OTAVehicle[]>([]);
  const [vts, setVts] = useState<RawVT[]>([]);
  const [componentCount, setComponentCount] = useState(0);
  const [sessions, setSessions] = useState<OTADeploymentSession[]>([]);

  const [vtFilter, setVtFilter] = useState("");
  const [fleetFilter, setFleetFilter] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [fleetsData, vehiclesData, vtsRes, components, sessionData] =
        await Promise.all([
          otaFleetsApi.getAll(),
          otaVehiclesApi.getAll(),
          API.get("/ota/ota-vehicle-types/"),
          otaComponentsApi.getAll(),
          deploymentSessionsApi.getAll(),
        ]);
      setFleets(fleetsData);
      setVehicles(vehiclesData);
      setVts(
        Array.isArray(vtsRes.data) ? vtsRes.data : vtsRes.data?.results ?? []
      );
      setComponentCount(components.length);
      setSessions(sessionData);
    } catch {
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) return <LoadingState>Loading statistics…</LoadingState>;

  // ── Filter options ────────────────────────────────────────────────────────────
  const vtOptions = unique(vts.map((v) => v.name));
  const fleetOptions = unique(fleets.map((f) => f.name));
  const isFiltered = vtFilter !== "" || fleetFilter !== "";

  // ── Filtered data ─────────────────────────────────────────────────────────────
  const filteredVehicles = vehicles.filter((v) => {
    if (vtFilter && v.vehicleTypeName !== vtFilter) return false;
    if (fleetFilter && v.fleetName !== fleetFilter) return false;
    return true;
  });

  const filteredSessions = sessions.filter((s) => {
    if (vtFilter && s.vehicleTypeName !== vtFilter) return false;
    if (fleetFilter && s.fleetName !== fleetFilter) return false;
    return true;
  });

  const filteredFleets = fleetFilter
    ? fleets.filter((f) => f.name === fleetFilter)
    : fleets;

  // ── Overview KPIs ─────────────────────────────────────────────────────────────
  const activeFleets = filteredFleets.filter((f) => f.isActive).length;
  const activeVehicles = filteredVehicles.filter((v) => v.isActive).length;
  const onlineVehicles = filteredVehicles.filter(
    (v) => v.connectionStatus === "online"
  ).length;
  const activeVTs = vts.filter((v) => !v.is_deprecated).length;

  const completedSessions = filteredSessions.filter(
    (s) => s.status === "COMPLETED"
  ).length;
  const failedSessions = filteredSessions.filter(
    (s) => s.status === "FAILED"
  ).length;
  const successRate =
    filteredSessions.length > 0
      ? Math.round((completedSessions / filteredSessions.length) * 100)
      : null;

  // ── Deployment Health (target-level sums from session list fields) ────────────
  const totalTargets = filteredSessions.reduce(
    (acc, s) => acc + (s.totalTargets ?? 0),
    0
  );
  const completedTargets = filteredSessions.reduce(
    (acc, s) => acc + (s.completedTargets ?? 0),
    0
  );
  const failedTargets = filteredSessions.reduce(
    (acc, s) => acc + (s.failedTargets ?? 0),
    0
  );
  const inProgressTargets = filteredSessions
    .filter((s) => s.status === "IN_PROGRESS")
    .reduce(
      (acc, s) =>
        acc +
        Math.max(
          0,
          (s.totalTargets ?? 0) -
            (s.completedTargets ?? 0) -
            (s.failedTargets ?? 0)
        ),
      0
    );

  // ── Breakdowns ────────────────────────────────────────────────────────────────
  const connectionBreakdown = countBy(
    filteredVehicles,
    (v) => v.connectionStatus || "unknown"
  );
  const sessionBreakdown = countBy(filteredSessions, (s) => s.status);

  const vehiclesByFleet = countBy(vehicles, (v) => v.fleetName || "Unassigned");

  // ── Recent failures ───────────────────────────────────────────────────────────
  const recentFailures = [...filteredSessions]
    .filter((s) => s.status === "FAILED")
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
    .slice(0, 5);

  return (
    <Container>
      {/* ── Header + Filters ──────────────────────────────────────────────────── */}
      <PageHeader>
        <PageTitle>Statistics</PageTitle>

        <FilterLabel>Vehicle Type:</FilterLabel>
        <FilterSelect
          value={vtFilter}
          onChange={(e) => setVtFilter(e.target.value)}
        >
          <option value="">All</option>
          {vtOptions.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </FilterSelect>

        <FilterLabel>Fleet:</FilterLabel>
        <FilterSelect
          value={fleetFilter}
          onChange={(e) => setFleetFilter(e.target.value)}
        >
          <option value="">All</option>
          {fleetOptions.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </FilterSelect>

        {isFiltered && (
          <ClearBtn
            onClick={() => {
              setVtFilter("");
              setFleetFilter("");
            }}
          >
            ✕ Clear
          </ClearBtn>
        )}

        <RefreshBtn onClick={fetchAll}>↻ Refresh</RefreshBtn>
      </PageHeader>

      {isFiltered && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {vtFilter && <FilterBadge>Vehicle Type: {vtFilter}</FilterBadge>}
          {fleetFilter && <FilterBadge>Fleet: {fleetFilter}</FilterBadge>}
        </div>
      )}

      {/* ── Overview KPIs ─────────────────────────────────────────────────────── */}
      <div>
        <SectionTitle>Overview</SectionTitle>
        <MetricsGrid>
          <MetricCard $accent="#55b8b3">
            <MetricLabel>Fleets</MetricLabel>
            <MetricValue>{filteredFleets.length}</MetricValue>
            <MetricSub>{activeFleets} active</MetricSub>
          </MetricCard>

          <MetricCard $accent="#3b82f6">
            <MetricLabel>Vehicles</MetricLabel>
            <MetricValue>{filteredVehicles.length}</MetricValue>
            <MetricSub>
              {activeVehicles} active · {onlineVehicles} online
            </MetricSub>
          </MetricCard>

          <MetricCard $accent="#8b5cf6">
            <MetricLabel>Vehicle Types</MetricLabel>
            <MetricValue>{vts.length}</MetricValue>
            <MetricSub>{activeVTs} active</MetricSub>
          </MetricCard>

          <MetricCard $accent="#f59e0b">
            <MetricLabel>Components</MetricLabel>
            <MetricValue>{componentCount}</MetricValue>
          </MetricCard>

          <MetricCard $accent="#10b981">
            <MetricLabel>Deployment Sessions</MetricLabel>
            <MetricValue>{filteredSessions.length}</MetricValue>
            {successRate !== null && (
              <MetricSub>{successRate}% success rate</MetricSub>
            )}
          </MetricCard>

          <MetricCard $accent={failedSessions > 0 ? "#f44336" : "#4caf50"}>
            <MetricLabel>Failed Sessions</MetricLabel>
            <MetricValue $color={failedSessions > 0 ? "#f44336" : "#4caf50"}>
              {failedSessions}
            </MetricValue>
            <MetricSub>of {filteredSessions.length} total</MetricSub>
          </MetricCard>
        </MetricsGrid>
      </div>

      {/* ── Deployment Health ─────────────────────────────────────────────────── */}
      {totalTargets > 0 && (
        <div>
          <SectionTitle>Deployment Health</SectionTitle>
          <HealthGrid>
            <MetricCard $accent="#64748b">
              <MetricLabel>Vehicle-Deployments</MetricLabel>
              <MetricValue>{totalTargets}</MetricValue>
              <MetricSub>across all sessions</MetricSub>
            </MetricCard>

            <MetricCard $accent="#4caf50">
              <MetricLabel>Completed</MetricLabel>
              <MetricValue $color="#4caf50">{completedTargets}</MetricValue>
              <MetricSub>
                {totalTargets > 0
                  ? Math.round((completedTargets / totalTargets) * 100)
                  : 0}
                % of total
              </MetricSub>
            </MetricCard>

            <MetricCard $accent="#f44336">
              <MetricLabel>Failed</MetricLabel>
              <MetricValue $color={failedTargets > 0 ? "#f44336" : "#4caf50"}>
                {failedTargets}
              </MetricValue>
              <MetricSub>
                {totalTargets > 0
                  ? Math.round((failedTargets / totalTargets) * 100)
                  : 0}
                % of total
              </MetricSub>
            </MetricCard>

            <MetricCard $accent="#2196f3">
              <MetricLabel>In Progress</MetricLabel>
              <MetricValue $color="#2196f3">{inProgressTargets}</MetricValue>
              <MetricSub>active right now</MetricSub>
            </MetricCard>
          </HealthGrid>
        </div>
      )}

      {/* ── Breakdowns ────────────────────────────────────────────────────────── */}
      <div>
        <SectionTitle>Breakdowns</SectionTitle>
        <BreakdownGrid>
          <BreakdownCard>
            <MetricLabel style={{ marginBottom: 12 }}>
              Vehicle Connectivity
            </MetricLabel>
            {Object.keys(connectionBreakdown).length === 0 ? (
              <MetricSub>No vehicles</MetricSub>
            ) : (
              Object.entries(connectionBreakdown)
                .sort((a, b) => b[1] - a[1])
                .map(([status, count]) => (
                  <BreakdownRow key={status}>
                    <BreakdownLabel>
                      <Dot $color={connectionColors[status] ?? "#9ca3af"} />
                      {status}
                    </BreakdownLabel>
                    <BreakdownCount>{count}</BreakdownCount>
                  </BreakdownRow>
                ))
            )}
          </BreakdownCard>

          {filteredSessions.length > 0 && (
            <BreakdownCard>
              <MetricLabel style={{ marginBottom: 12 }}>
                Sessions by Status
              </MetricLabel>
              {Object.entries(sessionBreakdown)
                .sort((a, b) => b[1] - a[1])
                .map(([status, count]) => (
                  <BreakdownRow key={status}>
                    <BreakdownLabel>
                      <Dot $color={sessionStatusColors[status] ?? "#9ca3af"} />
                      {status.replace("_", " ")}
                    </BreakdownLabel>
                    <BreakdownCount>{count}</BreakdownCount>
                  </BreakdownRow>
                ))}
            </BreakdownCard>
          )}

          {!fleetFilter && Object.keys(vehiclesByFleet).length > 0 && (
            <BreakdownCard>
              <MetricLabel style={{ marginBottom: 12 }}>
                Vehicles by Fleet
              </MetricLabel>
              {Object.entries(vehiclesByFleet)
                .sort((a, b) => b[1] - a[1])
                .map(([name, count]) => (
                  <BreakdownRow key={name}>
                    <BreakdownLabel>{name}</BreakdownLabel>
                    <BreakdownCount>{count}</BreakdownCount>
                  </BreakdownRow>
                ))}
            </BreakdownCard>
          )}
        </BreakdownGrid>
      </div>

      {/* ── Recent Failures ───────────────────────────────────────────────────── */}
      {recentFailures.length > 0 && (
        <div>
          <SectionTitle>Recent Failures</SectionTitle>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Fleet</Th>
                  <Th>Release</Th>
                  <Th>Failed / Total</Th>
                  <Th>Error</Th>
                  <Th>Date</Th>
                </tr>
              </thead>
              <tbody>
                {recentFailures.map((s) => (
                  <Tr key={s.id}>
                    <Td>{s.fleetName || "—"}</Td>
                    <Td>{s.releaseName || "—"}</Td>
                    <Td>
                      {s.failedTargets ?? "—"} / {s.totalTargets ?? "—"}
                    </Td>
                    <Td>
                      {s.errorMessage ? (
                        truncate(s.errorMessage, 80)
                      ) : (
                        <ErrorText>no message</ErrorText>
                      )}
                    </Td>
                    <Td>{s.createdAt ? formatDate(s.createdAt) : "—"}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        </div>
      )}
    </Container>
  );
});
